/**
 * Brand Configuration Types
 * Centralized configuration layer for brand settings
 * Includes company info, links, styling, and journey settings
 */

import type { ThemeConfig } from '@/types/config/theme';

/**
 * The language set the theme schema allows, derived from the generated config
 * type so it cannot drift from `theme.schema.json`.
 */
export type ThemeLanguage = NonNullable<ThemeConfig['defaultLanguage']>;

import { JOURNEY_DEFAULTS, THEME_DEFAULTS } from '@/lib/schema-utils';
import type { DesignLayout } from '@/types';

/**
 * Payment Plan Tier
 * Defines installment plan options for customers
 */
export interface PaymentPlanTier {
  id: string;
  name: string;
  description?: string;
  minimumAmount: number;
  maximumAmount?: number;
  maxInstallments: number;
  interestRate?: number;
  setupFee?: number;
}

/**
 * Brand Configuration
 * Combines company information, links, style settings, and journey settings
 */
export interface BrandConfiguration {
  // Company variables
  company_name: string | null;
  company_legal_name: string | null;
  company_phone: string | null;
  company_email: string | null;
  company_address: string | null;

  // Link variables
  support_url: string | null;
  support_email: string | null;
  support_phone: string | null;
  support_address: string | null;
  return_address: string | null;
  privacy_policy_url: string | null;
  terms_url: string | null;
  unsubscribe_url_template: string | null;

  // Journey footer links (up to 3)
  // name can be a simple string (backward compatible) or a localized object keyed by BCP47 locale (e.g., {"en-GB": "Secure", "pt-PT": "Seguro"})
  journey_footer_links?: Array<{
    name: string | Record<string, string>;
    url: string;
  }>;

  // Style variables
  theme: 'professional' | 'approachable';
  showGradientSphere?: boolean; // Show gradient sphere background effect (default: true for approachable theme, false for professional)
  logo?: {
    url: string;
    alt?: string;
  };
  logo_icon?: {
    light?: string; // Logo icon for light mode
    dark?: string; // Logo icon for dark mode
  };
  logo_full?: {
    light?: string; // Full logo for light mode
    dark?: string; // Full logo for dark mode
  };
  favicon?: {
    light?: string; // Favicon for light mode
    dark?: string; // Favicon for dark mode
  };
  colors: {
    brand: string; // Brand color (hex)
    // Light/Dark mode specific colors (only colors exposed in brand styles picker)
    buttonBackground?: {
      light?: string;
      dark?: string;
    };
    buttonText?: {
      light?: string;
      dark?: string;
    };
    linksColor?: {
      light?: string;
    };
    emailBackground?: {
      light?: string;
    };
    // Email design colors (can be overridden in theme config)
    text?: string; // Default text color
    muted?: string; // Muted background color
    border?: string; // Border color
    accent?: string; // Accent color for highlights
  };
  layout: DesignLayout | null;

  // Journey Settings (NEW)
  payment?: {
    allowPartialPayment: boolean;
    enablePaymentPlan: boolean;
    minimumPaymentAmount?: number;
    maximumPaymentAmount?: number;
  };
  paymentPlanTiers?: Array<PaymentPlanTier>;
  uiFeatures?: {
    showAccountBalance: boolean;
    showPaymentHistory: boolean;
    showDocuments: boolean;
    showDisputes: boolean;
    enableChat: boolean;
  };
  defaultLanguage?: ThemeLanguage;
  availableLanguages?: ThemeLanguage[];
}

/**
 * Scope Types
 */
export type ScopeType = 'operator' | 'debt_type' | 'client';

export interface Scope {
  type: ScopeType;
  operatorId: string;
  debtTypeId?: string;
  clientId?: string;
}

export interface ScopeOption {
  type: ScopeType;
  id: string;
  name: string;
  description?: string;
  operatorId?: string;
  debtTypeId?: string;
}

/**
 * Default brand configuration values
 * Defaults are sourced from shared schema-utils (single source of truth)
 */
export const DEFAULT_BRAND_CONFIG: BrandConfiguration = {
  // Company variables (no schema defaults - null by design)
  company_name: null,
  company_legal_name: null,
  company_phone: null,
  company_email: null,
  company_address: null,

  // Link variables (no schema defaults - null by design)
  support_url: null,
  support_email: null,
  support_phone: null,
  support_address: null,
  return_address: null,
  privacy_policy_url: null,
  terms_url: null,
  unsubscribe_url_template: null,

  // Journey footer links
  journey_footer_links: [],

  // Style variables - from THEME_DEFAULTS
  theme: THEME_DEFAULTS.theme,
  showGradientSphere: THEME_DEFAULTS.showGradientSphere,
  colors: {
    brand: THEME_DEFAULTS.colors.brand,
    buttonBackground: {
      light: THEME_DEFAULTS.colors.buttonBackground.light,
      dark: THEME_DEFAULTS.colors.buttonBackground.dark,
    },
    buttonText: {
      light: THEME_DEFAULTS.colors.buttonText.light,
      dark: THEME_DEFAULTS.colors.buttonText.dark,
    },
    linksColor: {
      light: THEME_DEFAULTS.colors.linksColor.light,
    },
    emailBackground: {
      light: THEME_DEFAULTS.colors.emailBackground.light,
    },
    text: THEME_DEFAULTS.colors.text,
    muted: THEME_DEFAULTS.colors.muted,
    border: THEME_DEFAULTS.colors.border,
    accent: THEME_DEFAULTS.colors.accent,
  },
  layout: {
    borderRadius: THEME_DEFAULTS.layout.borderRadius,
  },

  // Journey Settings - from JOURNEY_DEFAULTS
  payment: {
    allowPartialPayment: JOURNEY_DEFAULTS.payment.allowPartialPayment,
    enablePaymentPlan: JOURNEY_DEFAULTS.payment.enablePaymentPlan,
    minimumPaymentAmount: JOURNEY_DEFAULTS.payment.minimumPaymentAmount,
    maximumPaymentAmount: JOURNEY_DEFAULTS.payment.maximumPaymentAmount,
  },
  paymentPlanTiers: [],
  uiFeatures: {
    showAccountBalance: true,
    showPaymentHistory: true,
    showDocuments: true,
    showDisputes: true,
    enableChat: false,
  },
  defaultLanguage: 'en' as const,
  availableLanguages: ['en', 'pt'] as ThemeLanguage[],
};
